// /frontend/src/pages/PlansPricing.js  (v2.0 – modernized UI)
// All logic identical to v1.
import '../custom.css'

import React, { useState } from 'react'
import { CheckCircleFill } from 'react-bootstrap-icons'

/* ───────────────────────── static tier data ───────────────────────── */
const BASE_TIERS = [
  {
    key    : 'starter',
    name   : 'Starter',
    price  : 9,
    desc   : 'For creators and individuals getting started.',
    primary: false,
    features: [
      '15 posts / month',
      '8 bulk AI posts / month',
      '3 connected accounts',
      '10 AI credits / month',
      '5 GB file storage',
      'AI Post Creator',
      'Content Calendar',
      'Video Editor',
    ],
  },
  {
    key    : 'pro',
    name   : 'Pro',
    price  : 29,
    desc   : 'Everything you need to grow your presence.',
    primary: true,
    features: [
      '30 posts / month',
      '30 bulk AI posts / month',
      '10 connected accounts',
      '20 AI credits / month',
      '25 GB file storage',
      'AI Post Creator',
      'AI Caption Generator',
      'Content Calendar',
      'Video Editor',
      'Analytics & Insights',
      'No Greatly Social branding',
    ],
  },
  {
    key    : 'agency',
    name   : 'Agency',
    price  : 69,
    desc   : 'Built for agencies and teams at scale.',
    primary: false,
    features: [
      '100 posts / month',
      '100 bulk AI posts / month',
      '20 connected accounts',
      '30 AI credits / month',
      '100 GB file storage',
      'All Pro features',
      'Client approvals',
      'Priority support',
    ],
  },
]

const COMPARE_ROWS = [
  { label: 'Posts / month',          vals: ['15', '30', '100'] },
  { label: 'Bulk AI posts / month',  vals: ['8',  '30', '100'] },
  { label: 'Connected accounts',     vals: ['3',  '10', '20']  },
  { label: 'AI credits / month',     vals: ['10', '20', '30']  },
  { label: 'File storage',         vals: ['5 GB', '25 GB', '100 GB'] },
  { label: 'AI Post Creator',      vals: [true, true, true]  },
  { label: 'AI Caption Generator', vals: [false, true, true] },
  { label: 'Video Editor',         vals: [true, true, true]  },
  { label: 'Best-time scheduling', vals: [true, true, true]  },
  { label: 'Analytics & Insights', vals: [false, true, true] },
  { label: 'No Greatly Social branding', vals: [false, true, true] },
  { label: 'Client approvals',     vals: [false, false, true]},
  { label: 'Priority support',     vals: [false, false, true]},
]

const FAQS = [
  {
    q: 'What happens after my trial ends?',
    a: 'Your scheduled posts remain in your calendar. Choose a paid plan to keep publishing, or downgrade anytime — no pressure.',
  },
  {
    q: 'Can I change plans later?',
    a: 'Absolutely. Upgrade or downgrade at any time from your billing settings. Charges are prorated automatically.',
  },
  {
    q: 'Do you offer nonprofit discounts?',
    a: 'Yes! Verified nonprofits get 30% off any paid plan. Contact our support team with proof of status.',
  },
  {
    q: 'What counts as a post?',
    a: 'Each published or scheduled post to a single platform counts as one post. Posting the same content to 3 platforms counts as 3 posts.',
  },
  {
    q: 'What’s the difference between bulk AI posts and AI credits?',
    a: 'Bulk AI posts are your monthly allowance in the Bulk Post Creator — each post you generate there uses one, whether it includes an AI-generated photo, your own photo, or just a caption. AI credits power the other AI tools like the AI Post Creator and Caption Generator. Both reset monthly with your plan.',
  },
]

const btnPrimary = {
  display        : 'flex',
  alignItems     : 'center',
  justifyContent : 'center',
  gap            : 7,
  width          : '100%',
  padding        : '12px 0',
  borderRadius   : 10,
  border         : 'none',
  background     : '#2563eb',
  color          : '#ffffff',
  fontSize       : 14,
  fontWeight     : 700,
  cursor         : 'pointer',
  textDecoration : 'none',
  transition     : 'background 0.15s',
  boxShadow      : '0 4px 14px rgba(37,99,235,0.3)',
}

export default function PlansPricing () {
  const [yearly,   setYearly]   = useState(false)
  const [openFaq,  setOpenFaq]  = useState(null)

  const priceDisplay = p => yearly ? `$${Math.round(p * 8)}` : `$${p}`
  const period       = yearly ? '/ year' : '/ month'

  return (
    <div style={{ fontFamily:"'DM Sans', system-ui, sans-serif", color:'#111827' }}>

      {/* ═══════════════════════════════════════
          HERO
      ═══════════════════════════════════════ */}
      <section style={{
        background : 'linear-gradient(160deg, #0f172a 0%, #1e3a5f 60%, #1d4ed8 100%)',
        padding    : '72px 24px 80px',
        textAlign  : 'center',
        position   : 'relative',
        overflow   : 'hidden',
      }}>
        <div style={{ position:'absolute', top:-100, right:-100, width:400, height:400, borderRadius:'50%', background:'rgba(37,99,235,0.2)', filter:'blur(60px)', pointerEvents:'none' }} />
        <div style={{ position:'relative', zIndex:1, maxWidth:660, margin:'0 auto' }}>
          <div style={{ display:'inline-flex', alignItems:'center', gap:6, background:'rgba(255,255,255,0.1)', border:'1px solid rgba(255,255,255,0.15)', borderRadius:99, padding:'5px 16px', marginBottom:20 }}>
            <i className='fas fa-tag' style={{ fontSize:11, color:'#60a5fa' }} />
            <span style={{ fontSize:12, fontWeight:700, color:'rgba(255,255,255,0.8)', textTransform:'uppercase', letterSpacing:'0.07em' }}>14-day free trial on all plans</span>
          </div>
          <h1 style={{ fontSize:'clamp(2rem, 5vw, 3rem)', fontWeight:900, color:'#ffffff', marginBottom:14 }}>
            Simple, transparent pricing
          </h1>
          <p style={{ fontSize:17, color:'rgba(255,255,255,0.7)', marginBottom:32, lineHeight:1.6 }}>
            No hidden fees. No long-term contracts. Cancel anytime.
          </p>

          {/* Billing toggle */}
          <div style={{ display:'inline-flex', alignItems:'center', gap:0, background:'rgba(255,255,255,0.1)', borderRadius:12, padding:4 }}>
            {['Monthly', 'Yearly'].map(opt => {
              const isActive = (opt === 'Yearly') === yearly
              return (
                <button
                  key={opt}
                  onClick={() => setYearly(opt === 'Yearly')}
                  style={{
                    padding    : '8px 22px',
                    borderRadius: 9,
                    border     : 'none',
                    background : isActive ? '#ffffff' : 'transparent',
                    color      : isActive ? '#111827' : 'rgba(255,255,255,0.65)',
                    fontWeight : isActive ? 700 : 500,
                    fontSize   : 14,
                    cursor     : 'pointer',
                    boxShadow  : isActive ? '0 1px 4px rgba(0,0,0,0.15)' : 'none',
                    transition : 'all 0.15s',
                    position   : 'relative',
                  }}
                >
                  {opt}
                  {opt === 'Yearly' && (
                    <span style={{ position:'absolute', top:-10, right:-4, background:'#dcfce7', color:'#16a34a', fontSize:9, fontWeight:800, padding:'1px 6px', borderRadius:99, whiteSpace:'nowrap' }}>
                      SAVE 20%
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════
          PRICING CARDS
      ═══════════════════════════════════════ */}
      <section style={{ background:'#f8fafc', padding:'64px 24px' }}>
        <div style={{ maxWidth:1100, margin:'0 auto', display:'flex', flexWrap:'wrap', gap:20, justifyContent:'center' }}>
          {BASE_TIERS.map(t => (
            <div
              key={t.key}
              style={{
                flex        : '1 1 280px',
                maxWidth    : 340,
                background  : '#ffffff',
                border      : `2px solid ${t.primary ? '#2563eb' : '#e5e7eb'}`,
                borderRadius: 18,
                padding     : '32px 28px',
                position    : 'relative',
                boxShadow   : t.primary ? '0 12px 40px rgba(37,99,235,0.15)' : '0 1px 4px rgba(0,0,0,0.05)',
                display     : 'flex',
                flexDirection:'column',
              }}
            >
              {t.primary && (
                <div style={{ position:'absolute', top:-13, left:'50%', transform:'translateX(-50%)', background:'#2563eb', color:'#fff', fontSize:11, fontWeight:800, padding:'3px 16px', borderRadius:99, whiteSpace:'nowrap', letterSpacing:'0.04em' }}>
                  ⚡ Most Popular
                </div>
              )}

              <div style={{ fontSize:11, fontWeight:800, textTransform:'uppercase', letterSpacing:'0.1em', color:'#9ca3af', marginBottom:8 }}>{t.name}</div>
              <p style={{ fontSize:13, color:'#6b7280', marginBottom:20, lineHeight:1.5 }}>{t.desc}</p>

              <div style={{ display:'flex', alignItems:'flex-end', gap:3, marginBottom:4 }}>
                <span style={{ fontSize:48, fontWeight:900, color:'#111827', lineHeight:1 }}>{priceDisplay(t.price)}</span>
              </div>
              <div style={{ fontSize:13, color:'#9ca3af', marginBottom:24 }}>
                USD {period}
                {yearly && <span style={{ color:'#16a34a', fontWeight:600, marginLeft:6 }}>· Save 20%</span>}
              </div>

              <a
                href='/register'
                style={{
                  ...btnPrimary,
                  background : t.primary ? '#2563eb' : 'transparent',
                  border     : t.primary ? 'none' : '1.5px solid #2563eb',
                  color      : t.primary ? '#fff' : '#2563eb',
                  boxShadow  : t.primary ? '0 4px 14px rgba(37,99,235,0.3)' : 'none',
                  marginBottom: 24,
                }}
                onMouseEnter={e => { if (t.primary) e.currentTarget.style.background='#1d4ed8' }}
                onMouseLeave={e => { if (t.primary) e.currentTarget.style.background='#2563eb' }}
              >
                Start for Free
              </a>

              <div style={{ borderTop:'1px solid #f3f4f6', paddingTop:20, flex:1 }}>
                {t.features.map(f => (
                  <div key={f} style={{ display:'flex', alignItems:'center', gap:9, marginBottom:10, fontSize:13, color:'#374151' }}>
                    <i className='fas fa-check' style={{ fontSize:10, color:'#16a34a', flexShrink:0 }} />
                    {f}
                  </div>
                ))}
              </div>

              {/* Platform icons */}
              <div style={{ display:'flex', gap:8, marginTop:20, paddingTop:16, borderTop:'1px solid #f3f4f6', flexWrap:'wrap' }}>
                {['facebook-f','instagram','x-twitter','linkedin-in','pinterest','tiktok','youtube'].map(icon => (
                  <i key={icon} className={`fab fa-${icon}`} style={{ fontSize:13, color:'#9ca3af' }} />
                ))}
              </div>
            </div>
          ))}
        </div>

        <p style={{ textAlign:'center', fontSize:13, color:'#9ca3af', marginTop:24 }}>
          All plans include a 14-day free trial. No credit card required.
        </p>
      </section>

      {/* ═══════════════════════════════════════
          COMPARISON TABLE
      ═══════════════════════════════════════ */}
      <section style={{ background:'#ffffff', padding:'72px 24px' }}>
        <div style={{ maxWidth:900, margin:'0 auto' }}>
          <h2 style={{ fontSize:'clamp(1.5rem, 4vw, 2rem)', fontWeight:900, textAlign:'center', marginBottom:40 }}>
            Compare all plans
          </h2>

          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead>
                <tr>
                  <th style={{ padding:'12px 16px', textAlign:'left', fontSize:13, color:'#9ca3af', fontWeight:700, borderBottom:'2px solid #e5e7eb' }}>Feature</th>
                  {BASE_TIERS.map(t => (
                    <th key={t.key} style={{
                      padding    : '12px 16px',
                      textAlign  : 'center',
                      fontSize   : 14,
                      fontWeight : 800,
                      color      : t.primary ? '#2563eb' : '#111827',
                      borderBottom:'2px solid #e5e7eb',
                      background : t.primary ? '#f0f7ff' : 'transparent',
                      borderRadius: t.primary ? '8px 8px 0 0' : 0,
                    }}>
                      {t.name}
                      {t.primary && <div style={{ fontSize:10, color:'#2563eb', fontWeight:600, marginTop:2 }}>Most Popular</div>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {COMPARE_ROWS.map((row, ri) => (
                  <tr key={row.label} style={{ background: ri % 2 === 0 ? '#fafafa' : '#ffffff' }}>
                    <td style={{ padding:'12px 16px', fontSize:13, color:'#374151', fontWeight:500, borderBottom:'1px solid #f3f4f6' }}>{row.label}</td>
                    {row.vals.map((v, i) => (
                      <td key={i} style={{
                        padding    : '12px 16px',
                        textAlign  : 'center',
                        fontSize   : 13,
                        color      : '#374151',
                        borderBottom:'1px solid #f3f4f6',
                        background : BASE_TIERS[i].primary ? 'rgba(37,99,235,0.03)' : 'transparent',
                        fontWeight : typeof v === 'string' ? 600 : 400,
                      }}>
                        {typeof v === 'boolean'
                          ? v
                            ? <i className='fas fa-check' style={{ color:'#16a34a', fontSize:13 }} />
                            : <span style={{ color:'#d1d5db', fontSize:16 }}>—</span>
                          : v
                        }
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════
          FAQ
      ═══════════════════════════════════════ */}
      <section style={{ background:'#f8fafc', padding:'72px 24px' }}>
        <div style={{ maxWidth:700, margin:'0 auto' }}>
          <h2 style={{ fontSize:'clamp(1.5rem, 4vw, 2rem)', fontWeight:900, textAlign:'center', marginBottom:40 }}>
            Pricing FAQs
          </h2>
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {FAQS.map((faq, i) => {
              const isOpen = openFaq === i
              return (
                <div
                  key={i}
                  style={{ background:'#ffffff', border:'1px solid #e5e7eb', borderRadius:12, overflow:'hidden', boxShadow:'0 1px 3px rgba(0,0,0,0.04)' }}
                >
                  <button
                    type='button'
                    onClick={() => setOpenFaq(isOpen ? null : i)}
                    style={{ width:'100%', display:'flex', alignItems:'center', justifyContent:'space-between', padding:'16px 20px', background:'transparent', border:'none', cursor:'pointer', textAlign:'left' }}
                  >
                    <span style={{ fontSize:14, fontWeight:700, color:'#111827' }}>{faq.q}</span>
                    <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'}`} style={{ fontSize:11, color:'#9ca3af', flexShrink:0, marginLeft:12 }} />
                  </button>
                  {isOpen && (
                    <div style={{ padding:'0 20px 16px', fontSize:14, color:'#6b7280', lineHeight:1.7 }}>
                      {faq.a}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════
          CTA
      ═══════════════════════════════════════ */}
      <section style={{ background:'linear-gradient(135deg, #1d4ed8, #2563eb)', padding:'72px 24px', textAlign:'center' }}>
        <div style={{ maxWidth:640, margin:'0 auto' }}>
          <h2 style={{ fontSize:'clamp(1.6rem, 4vw, 2.4rem)', fontWeight:900, color:'#ffffff', marginBottom:14 }}>
            Ready to grow faster on social?
          </h2>
          <p style={{ fontSize:17, color:'rgba(255,255,255,0.75)', marginBottom:32, lineHeight:1.6 }}>
            Start your free 14-day trial. No credit card required.
          </p>
          <a
            href='/register'
            style={{
              ...btnPrimary,
              width      : 'auto',
              display    : 'inline-flex',
              padding    : '14px 36px',
              background : '#ffffff',
              color      : '#1d4ed8',
              fontSize   : 16,
              boxShadow  : '0 4px 20px rgba(0,0,0,0.2)',
            }}
            onMouseEnter={e => e.currentTarget.style.background='#f0f9ff'}
            onMouseLeave={e => e.currentTarget.style.background='#ffffff'}
          >
            <i className='fas fa-arrow-right' style={{ fontSize:13 }} />
            Start Free Trial
          </a>
          <div style={{ display:'flex', justifyContent:'center', gap:24, marginTop:20, flexWrap:'wrap' }}>
            {['No credit card required', 'Cancel anytime', '14-day free trial'].map(t => (
              <span key={t} style={{ display:'flex', alignItems:'center', gap:6, fontSize:13, color:'rgba(255,255,255,0.55)' }}>
                <CheckCircleFill size={12} color='#86efac' />{t}
              </span>
            ))}
          </div>
        </div>
      </section>
    </div>
  )
}